import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Download, Image, Film, BarChart3, Cpu, Eye, Calendar, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_ICONS = { ai_image: Image, '3d_render': Cpu, data_viz: BarChart3, video_render: Film };
const SERVICE_LABELS = { ai_image: 'AI Image', '3d_render': '3D Render', data_viz: 'Data Viz', video_render: 'Video' };
const SERVICE_COLORS = { ai_image: '#FF6B4A', '3d_render': '#4A7CFF', data_viz: '#8B5CF6', video_render: '#34B76F' };

export default function Results() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewJob, setPreviewJob] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/dashboard/results`, { withCredentials: true });
        setResults(res.data);
      } catch (err) {
        console.warn('[results] load failed', err?.message);
      }
      finally { setLoading(false); }
    })();
  }, []);

  const handleDownload = async (job) => {
    setDownloading(job.job_id);
    try {
      const res = await axios.get(`${API}/dashboard/results/${job.job_id}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const ext = job.output_path?.split('.').pop() || 'png';
      a.download = `${job.title.replace(/\s+/g, '_')}_${job.job_id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('[results] download failed', err?.message);
      toast.error(err?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading('');
    }
  };

  const handlePreview = async (job) => {
    if (!job.output_path) return;
    try {
      const res = await axios.get(`${API}/dashboard/results/${job.job_id}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(url);
      setPreviewJob(job);
    } catch (err) {
      console.warn('[results] preview failed', err?.message);
      toast.error('Could not load preview');
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewJob(null);
  };

  const formatDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-5 h-5 text-[#8A8A9A] animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="results-page">
      <div>
        <p className="text-xs font-semibold text-[#FF6B4A] tracking-widest uppercase mb-1">Gallery</p>
        <h1 className="font-display text-3xl font-bold text-[#1A1A2E]" data-testid="results-heading">
          Rendered Results
        </h1>
        <p className="text-sm text-[#8A8A9A] mt-1">{results.length} completed render{results.length !== 1 ? 's' : ''}</p>
      </div>

      {results.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((job) => {
            const Icon = SERVICE_ICONS[job.service_type] || Image;
            const color = SERVICE_COLORS[job.service_type] || '#FF6B4A';
            const hasOutput = !!job.output_path;
            return (
              <div key={job.job_id} className="rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover-lift group" data-testid={`result-${job.job_id}`}>
                {/* Preview area */}
                <div className="h-44 flex items-center justify-center relative cursor-pointer" style={{ background: `${color}08` }} onClick={() => hasOutput && handlePreview(job)}>
                  <Icon className="w-14 h-14 transition-transform group-hover:scale-110" style={{ color: `${color}40` }} />
                  {hasOutput && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/10 transition-colors">
                      <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                  <Badge className="absolute top-3 left-3 text-[9px] rounded-full px-2 py-0 border" style={{ color, borderColor: `${color}30`, background: `${color}10` }}>
                    {SERVICE_LABELS[job.service_type]}
                  </Badge>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-[#1A1A2E] truncate mb-1">{job.title}</h3>
                  <div className="flex items-center gap-3 text-[10px] text-[#8A8A9A] mb-3">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(job.updated_at)}</span>
                    <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {job.cost} cr</span>
                    {job.file_info && <span>{formatSize(job.file_info.size)}</span>}
                  </div>

                  <Button
                    onClick={() => handleDownload(job)}
                    disabled={!hasOutput || downloading === job.job_id}
                    className="w-full bg-[#1A1A2E] text-white rounded-full text-xs h-9 font-semibold hover:bg-[#2A2A3E] disabled:opacity-40"
                    data-testid={`download-${job.job_id}`}
                  >
                    {downloading === job.job_id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <><Download className="w-3.5 h-3.5 mr-1.5" /> {hasOutput ? 'Download HD' : 'No Output'}</>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-20 rounded-2xl bg-white border border-gray-100">
          <Image className="w-12 h-12 text-[#8A8A9A]/30 mx-auto mb-4" />
          <p className="text-sm text-[#8A8A9A] mb-1">No rendered results yet.</p>
          <p className="text-xs text-[#8A8A9A]/60">Completed jobs will appear here with download options.</p>
        </div>
      )}

      {/* Full-screen preview modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8" onClick={closePreview} data-testid="preview-modal">
          <div className="relative max-w-4xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <img src={previewUrl} alt={previewJob?.title} className="max-w-full max-h-[80vh] rounded-xl shadow-2xl object-contain" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6 rounded-b-xl">
              <h3 className="text-white font-semibold text-lg">{previewJob?.title}</h3>
              <p className="text-white/60 text-xs mt-1">{SERVICE_LABELS[previewJob?.service_type]} &middot; {previewJob?.job_id}</p>
            </div>
            <Button onClick={closePreview} className="absolute top-4 right-4 bg-white/20 text-white rounded-full w-10 h-10 hover:bg-white/30 text-lg font-bold">
              &times;
            </Button>
            <Button
              onClick={() => handleDownload(previewJob)}
              className="absolute top-4 left-4 bg-[#FF6B4A] text-white rounded-full h-9 px-5 text-xs font-semibold hover:bg-[#FF5533]"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download HD
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
